import { useEffect, useState } from "react";
import axios from "axios";
import { ExternalLink, Star } from "lucide-react";
import TestimonialCard from "@/components/TestimonialCard";
import ConsentsCounter from "@/components/ConsentsCounter";
import GoogleReviewsBadge, { GOOGLE_REVIEWS_URL } from "@/components/GoogleReviewsBadge";
import SEO, { ORGANISATION_SCHEMA, SITE_URL } from "@/components/SEO";
import { API } from "@/lib/apiBase";

export default function TestimonialsPage() {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const response = await axios.get(`${API}/testimonials`);
        setTestimonials(response.data);
      } catch (error) {
        console.error("Error fetching testimonials:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchTestimonials();
  }, []);

  return (
    <div className="page-wrapper bg-ink" data-testid="testimonials-page">
      <SEO
        title="Client Reviews — 5-Star Rated on Google"
        description="See what Auckland property owners, developers and architects say about Code Planning Limited. Consistently 5-star reviewed across residential, subdivision, heritage and commercial consents."
        path="/testimonials"
        keywords="code planning reviews, auckland planner reviews, 5 star planning consultant"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: "Client Testimonials — Code Planning Limited",
            url: `${SITE_URL}/testimonials`,
            itemListElement: testimonials.map((t, idx) => ({
              "@type": "Review",
              position: idx + 1,
              author: { "@type": "Person", name: t.name },
              reviewBody: t.content,
              reviewRating: { "@type": "Rating", ratingValue: t.rating || 5, bestRating: 5 },
              itemReviewed: { "@id": `${SITE_URL}/#organization` },
            })),
          },
          ORGANISATION_SCHEMA,
        ]}
      />
      {/* Header */}
      <section className="py-12 md:py-16 bg-neutral-900">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-sm uppercase tracking-widest text-gray-400 mb-4">
            Client Stories
          </p>
          <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6">
            5-Star Google Reviewed.
            <br />
            <span className="italic text-gray-300">Many times over.</span>
          </h1>
          <p className="text-lg md:text-xl text-gray-400 max-w-2xl leading-relaxed mb-8">
            Don't just take our word for it. Here's what our clients have to say 
            about working with Code Planning Limited.
          </p>
          <div className="flex flex-wrap items-center gap-4">
            <GoogleReviewsBadge size="lg" />
            <a
              href={GOOGLE_REVIEWS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-white border-b border-white/40 hover:border-white pb-1 transition-colors duration-200 text-sm uppercase tracking-widest font-medium"
              data-testid="read-google-reviews-link"
            >
              Read all reviews on Google
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* Testimonials Grid */}
      <section className="py-10 md:py-14 bg-ink">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-neutral-800 animate-pulse h-[300px]" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {testimonials.map((testimonial) => (
                <TestimonialCard key={testimonial.id} testimonial={testimonial} />
              ))}
            </div>
          )}

          {/* CTA strip */}
          <div className="mt-10 md:mt-12 border border-neutral-800 bg-neutral-900 p-8 md:p-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div>
              <div className="flex items-center gap-1 mb-3">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
              <h3 className="font-heading text-2xl md:text-3xl font-semibold text-white">
                See every review for yourself.
              </h3>
              <p className="text-gray-400 mt-1">
                Our full review history lives publicly on Google.
              </p>
            </div>
            <a
              href={GOOGLE_REVIEWS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary inline-flex items-center gap-2 flex-shrink-0"
              data-testid="cta-google-reviews"
            >
              View on Google
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* Stats — matched to homepage */}
      <section className="py-10 md:py-14 bg-neutral-900 text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-8 text-center">
            <div>
              <p className="font-heading text-5xl md:text-6xl font-bold mb-2">40+</p>
              <p className="text-sm uppercase tracking-widest text-gray-400">
                Years Combined Experience
              </p>
            </div>
            <div data-testid="testimonials-counter-block">
              <p className="font-heading text-5xl md:text-6xl font-bold mb-2">
                <ConsentsCounter />
              </p>
              <p className="text-sm uppercase tracking-widest text-gray-400">
                Successful Resource Consents
              </p>
              <p className="text-xs uppercase tracking-widest text-gray-500 mt-1">
                & counting
              </p>
            </div>
            <div>
              <p className="font-heading text-5xl md:text-6xl font-bold mb-2">99%</p>
              <p className="text-sm uppercase tracking-widest text-gray-400">
                Success Rate
              </p>
            </div>
          </div>
          <p className="text-center text-gray-400 italic mt-12 max-w-2xl mx-auto text-lg">
            Each one a project we navigated personally — not outsourced, not automated.
          </p>
        </div>
      </section>
    </div>
  );
}
